import { useState, useEffect } from 'react';
import { AlertTriangle, X, ArrowRight } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';

const DISMISS_KEY = 'overage_banner_dismissed_at';

export function OverageBanner() {
  const { usageData, isLoading } = useSubscription();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) {
      setDismissed(false);
      return;
    }
    const dismissedDate = new Date(dismissedAt);
    const now = new Date();
    const hoursSince = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60);
    if (hoursSince >= 24) {
      localStorage.removeItem(DISMISS_KEY);
      setDismissed(false);
    }
  }, []);

  if (isLoading || dismissed) return null;
  if (!usageData?.has_subscription) return null;
  if (usageData.overage.total_overage_cost <= 0) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setDismissed(true);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

  const overCategories: string[] = [];
  if (usageData.overage.leads.over_by > 0) overCategories.push('leads');
  if (usageData.overage.calls.over_by > 0) overCategories.push('calls');
  if (usageData.overage.whatsapp.over_by > 0) overCategories.push('WhatsApp');
  if (usageData.overage.emails.over_by > 0) overCategories.push('emails');
  if (usageData.overage.linkedin.over_by > 0) overCategories.push('LinkedIn');

  const categoryText = overCategories.length > 0
    ? overCategories.join(', ')
    : 'usage';

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 px-4 py-2.5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-sm text-amber-900 truncate">
            <span className="font-semibold">Overage detected</span>
            <span className="mx-1.5 text-amber-400">|</span>
            You've exceeded your plan limits for {categoryText}.
            Current overage: <span className="font-bold">{formatCurrency(usageData.overage.total_overage_cost)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/settings?section=usage-limits')}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
          >
            View Details <ArrowRight className="w-3 h-3" />
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 text-amber-500 hover:text-amber-700 hover:bg-amber-100 rounded-lg transition-colors"
            title="Dismiss for 24 hours"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
